import React from "preact/compat";
import { HeaderProps } from "@/elements-react/catalog-layout/header";
import defaultStyles from "@/elements-react/catalog-layout/header/css/header.module.scss";
import styles from "../css/header.black-and-blue.module.scss";
import { FixBrand } from "@/elements-react/fix-brand";
import { LayoutStyleTypes } from "@/util-react/layout.tsx";
import { FixLink } from "@/elements-react/fix-link";
import { Url } from "@/helpers/url.ts";
import classNames from "classnames";
import {
    FixDropdownInputCountry,
    FixDropdownInputLanguage
} from "@/elements-react/fix-dropdown-connected";
import CustomerHead from "@/elements-react/customer-head";
import CartControlDropdown from "@/elements-react/cart-control-dropdown";
import { BannerSearch } from "@/elements-react/banner-search";
import TruckIcon from "@/assets/benefits/truck.svg";
import ShieldIcon from "@/assets/benefits/shield.svg";
import ReturnIcon from "@/assets/benefits/return.svg";
import { Icon } from "@/elements-react/fix-icon";
import StockBackground from "@/assets/stock_background.webp";
import { useServices } from "@/services-context.tsx";
import { FixSearch } from "@/elements-react/fix-search";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "preact/hooks";
import MenuSm from "@/elements-react/menu/MenuSm.tsx";
import { FixButton } from "@/elements-react/fix-button";
import menuSvg from "@/assets/menu.svg";
import { BackShoppingLink } from "@/elements-react/back-shopping-link";
import { Input } from "@/uikit";
import { Gtag } from "@/services/gtag.ts";
import { B2bHero } from "@/elements-react/b2b-hero";
import { B2bHeroMobile } from "@/elements-react/b2b-hero/b2b-hero-mobile";

const useTemplateData = (isMobile = false) => {
    const { t } = useTranslation();

    const benefitsList = useMemo(() => {
        const iconSize = isMobile ? 17 : 25;
        return [
            {
                icon: <Icon src={TruckIcon as string} width={iconSize} height={iconSize} />,
                label: t("mnbjeijrb12kfd.benefit-delivery" as any, "Fast EU delivery" as any),
                text: t("sdf3kjvn34obfd.benefit-delivery-text" as any, "Ships same day" as any)
            },
            {
                icon: <Icon src={ShieldIcon as string} width={iconSize} height={iconSize} />,
                label: t("mvbjrwhibvu3k.benefit-warranty" as any, "2-Year warranty" as any),
                text: t("ioksfdjfg324g.benefit-warranty-text" as any, "On all parts" as any),
            },
            {
                icon: <Icon src={ReturnIcon as string} width={iconSize} height={iconSize} />,
                label: t("mpepk39oikgrw.benefit-returns" as any, "Free returns" as any),
                text: t("9ikgrnbvjnfsf.benefit-returns-text" as any, "Within 30 days" as any),
            }
        ];
    }, [isMobile]);

    const homePageBannerContent = useMemo(() => (
        <div style={{ display: 'contents' }}>
            <div className={styles["center-row"]}>
                <div className={styles["parts-available-counter"]}> 1 152 194 {t("o093jvndsuew.parts-available" as any, "parts available today" as any)}</div>
                <div className={styles["banner-label"]}>
                    {t("gdbo2340ikmsjv4.banner-label" as any, "Cash & Carry for auto parts" as any)}
                </div>
                <div className={styles["banner-subtext"]}>
                    {t("gbbo2fk3vbl3xcd.banner-subtext" as any, "Real-time stock for garages, retailers and distributors" as any)}
                </div>
                <div className={styles["banner-search"]}>
                    <BannerSearch size={isMobile ? 'sm' : 'lg'} />
                    <a className={styles["banner-search-help"]} href={'#find-form'}>
                        {t("kjbkoern3ijgds.banner-search-help" as any, "Don’t know the part number?" as any)}
                    </a>
                </div>
            </div>
            <div className={styles["footer-row"]}>
                {benefitsList.map(({ icon, label, text }) => (
                    <div className={styles["benefit-item"]}>
                        <div className={styles["benefit-icon"]}>
                            {icon}
                        </div>
                        <div className={styles["benefit-info"]}>
                            <div className={styles["benefit-label"]}>
                                {label}
                            </div>
                            <div className={styles["benefit-text"]}>
                                {text}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    ), [isMobile]);

    return {
        benefitsList,
        homePageBannerContent
    };
}

export const HeaderDesktopBlackAndBlue: React.FC<HeaderProps> = ({ countryElementRefCallback, simple }) => {
    const { router } = useServices();
    const isHomePage = router.isHomePage();
    const isB2BPage = router.isB2BPage && router.isB2BPage();
    const { t } = useTranslation();

    const { homePageBannerContent } = useTemplateData();
    const headerClass = classNames(
        styles["head-bg"],
        {
            [styles["banner-variant"]]: isHomePage,
            [styles["b2b-variant"]]: isB2BPage
        });

    // Define handle functions for B2bHero
    const handleContactClick = () => {
        console.log("Contact sales clicked");
    };

    const handleBusinessClick = () => {
        console.log("Become business customer clicked");
    };

    return (
        <div className={headerClass}>
            {isHomePage && (
                <img src={StockBackground as string} alt="Header Background" className={styles["header-background"]} />
            )}
            <div className={styles["head-root"]}>
                <div className={classNames(defaultStyles["first-row"], styles["first-row"])}>
                    {
                        simple && <BackShoppingLink />
                    }
                    <div className={classNames(defaultStyles.brand, { [defaultStyles['centered']]: simple })}>
                        <FixLink href={Url.to("home-page-with-lang")} ariaLabel="logo">
                            <FixBrand type={LayoutStyleTypes.TYPE_LG} className={styles["brand-logo"]} />
                        </FixLink>
                    </div>
                    {
                        !simple && (
                            <div className={classNames(styles.center, styles['control-label'])}>
                                {/*<FixDropdownInputLanguage />*/}
                                <FixDropdownInputCountry ref={countryElementRefCallback} />
                            </div>
                        )
                    }
                    {!isHomePage && !isB2BPage && !simple && (
                        <div className={styles.search}>
                            <FixSearch type={LayoutStyleTypes.TYPE_LG} />
                        </div>
                    )}
                    <div className={classNames(defaultStyles.controls, defaultStyles.right)}>
                        {isHomePage && (
                            <div style={{ display: 'contents' }}>
                                <FixLink className={styles["header-link"]} href={Url.to("b2b-platform")}>{t("goiiroej43234bi4.for-business" as any, "For business" as any)}</FixLink>
                                <FixLink target="_blank" href="/business-v2" className={styles["header-link"]}>{t("basyf8oib4rn8934.b2b-portal" as any, "B2B Portal" as any)}</FixLink>
                            </div>
                        )}
                        {!simple && <CustomerHead labelClassName={styles['control-label']} />}
                        {!simple && <CartControlDropdown labelClassName={styles['control-label']} />}
                    </div>
                </div>
                {isHomePage && homePageBannerContent}
                {isB2BPage && (
                    <B2bHero
                        onContactClick={handleContactClick}
                        onBusinessClick={handleBusinessClick}
                    />
                )}
            </div>
            {!isHomePage && !isB2BPage && !simple && (<div className={defaultStyles.delimiter}></div>)}
        </div>
    )
}

export const HeaderMobileBlackAndBlue: React.FC<HeaderProps> = ({ simple }) => {
    const { homePageBannerContent } = useTemplateData(true);
    const [popup, setPopup] = useState<null | { target: Element }>(null);
    const { router } = useServices();
    const isHomePage = router.isHomePage();
    const isB2BPage = router.isB2BPage && router.isB2BPage();
    const { t } = useTranslation();
    const openPopup = (target: Element) => setPopup({ target });
    const closePopup = () => setPopup(null);

    const [searchValue, setSearchValue] = useState<string>(typeof window !== "undefined" ? Url.getQueryParam("q", window.location.search) ?? "" : "");
    const [hasSearched, setHasSearched] = useState(false);

    useEffect(() => {
        if (popup) {
            document.body.classList.add("no-scroll");
        } else {
            document.body.classList.remove("no-scroll");
        }
    }, [popup]);

    const headerClass = classNames(
        styles["head-bg"],
        styles["mobile-header"],
        {
            [styles["banner-variant"]]: isHomePage,
            [styles["b2b-variant"]]: isB2BPage,
            [styles["simple"]]: simple
        });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target!.value);
    };

    const search = () => {
        const trimmed = searchValue.trim();
        if (hasSearched || !trimmed) return;
        setHasSearched(true);
        Gtag.pushEvent("search_input", searchValue);
        window.location.href = Url.to("search-page", {}, `?q=${encodeURIComponent(searchValue)}`);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            search();
        }
    };

    const handleBlur = () => {
        search();
    };

    const handleBusinessClick = () => {
        const registrationUrl = "/auth/register?type=business";
        window.location.href = registrationUrl;
    };

    const handleContactClick = () => {
        window.open("https://wa.me/YOUR_NUMBER", "_blank");
    };

    return (
        <div className={headerClass}>
            {isHomePage && (
                <img src={StockBackground as string} alt="Header Background" className={styles["header-background"]} />
            )}
            {popup && <MenuSm onClose={closePopup} />}
            <div className={styles["head-root"]}>
                <div className={classNames(defaultStyles["first-row"], styles["first-row"])}>
                    <FixButton className={styles["menu"]} onClick={(e) => openPopup(e.currentTarget)} variant={'text'} aria-label="Open menu">
                        <Icon width={24} src={menuSvg as string} />
                    </FixButton>
                    {!isHomePage && !isB2BPage && !simple && (
                        <Input
                            name="mpn-search"
                            placeholder={t("kmaqihq7zxarr34z.search-placeholder" as any, "Search for part number" as any)}
                            value={searchValue}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onKeyDown={handleKeyDown}
                            onIconClick={search}
                            icon={<Icon width={16} name="search" />}
                            className={styles.search}
                            inputSize={'small'}
                            iconPosition={'prefix'}
                        />
                    )}
                    <div className={classNames(defaultStyles.brand, styles["brand"])}>
                        <FixLink href={Url.to("home-page-with-lang")} ariaLabel="logo">
                            <FixBrand type={LayoutStyleTypes.TYPE_LG} className={styles["brand-logo"]} />
                        </FixLink>
                    </div>
                    {!simple && (
                        <div className={styles.controls}>
                            <CartControlDropdown hideTotal={true} labelClassName={styles['control-label']} />
                        </div>
                    )}
                </div>
                {isHomePage && homePageBannerContent}
                {isB2BPage && (
                    <B2bHeroMobile
                        onBusinessClick={handleBusinessClick}
                        onContactClick={handleContactClick}
                    />
                )}
            </div>
            {simple && (<div className={defaultStyles.delimiter}></div>)}
        </div>
    )
}
